import {
  ArrowLeft,
  Building2,
  Check,
  ChevronsUpDown,
  Compass,
  LogOut,
  Settings,
  Sparkles,
  UserCircle,
} from "lucide-react";
import { useState } from "react";

import { useGuidanceVisibility } from "./use-guidance-visibility";
import { usePricingUnit, PRICING_UNITS } from "./use-pricing-unit";
import { useGasUnit } from "./use-gas-unit";
import { useMaxwellSurface } from "@/components/maxwell/useMaxwellSurface";
import { InitialsAvatar } from "@/components/ui/initials-avatar";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLogout } from "@/hooks/useLogout";
import { useSessionTenants, useSwitchTenant } from "@/hooks/useSessionTenants";
import { useMaxwellAccess } from "@/components/maxwell/useMaxwellAccess";
import { formatGasRateIn, formatRateInUnit, pricingUnitLabel } from "@/lib/format";
import { useAuthSession } from "@/lib/session-context";
import {
  MODE_VARIANTS,
  PALETTE_VARIANTS,
  useTheme,
  type Mode,
  type PaletteId,
} from "@/lib/theme";
import { cn } from "@/lib/utils";
import { GAS_UNIT_PICKER_LABELS, GAS_UNITS, type GasUnit } from "@/types/gas-units";
import type { PricingUnit } from "@/types/generated/PricingUnit";

/**
 * Bottom-of-sidebar user widget. Click pops a popover upward with two
 * views that swap inside the same surface:
 *
 *   - **menu** — identity header, Profile (stub), Preferences, Switch
 *     tenant (super admins), Sign out.
 *   - **prefs** — a wide three-column grid: Look (mode, palette), Units
 *     (electric, gas), Workspace (Maxwell, guidance).
 *   - **tenants** — the tenants a super admin belongs to; picking one moves
 *     the session there (AB#8687).
 *
 * Single Popover instead of nesting a Popover inside a DropdownMenu —
 * two portalled surfaces fight each other's outside-click detection and
 * focus traps, which caused the prefs panel to flash and immediately
 * close. One surface, internal `view` state, smooth swap.
 *
 * Compact mode collapses the trigger to just the avatar disc; the
 * popover content is identical to expanded mode.
 */
type View = "menu" | "prefs" | "tenants";

export function UserMenu({ compact }: { compact: boolean }) {
  const session = useAuthSession();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("menu");

  // Reset to the menu view every time the popover closes so the next
  // open starts at the top level — never strands the user in prefs.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setView("menu");
  };

  const triggerInner = compact ? (
    <>
      <InitialsAvatar
        name={session.display_name}
        initials={session.initials}
        size={28}
      />
      <span className="absolute bottom-1 right-1 h-2 w-2 rounded-full bg-success ring-2 ring-background-deep" />
    </>
  ) : (
    <>
      <div className="relative">
        <InitialsAvatar
          name={session.display_name}
          initials={session.initials}
          size={28}
        />
        {/* Dot ring punches against the sidebar fill (`background-deep`)
            on rest, swaps to the hover/active fill (`row-hover`) when the
            row is hovered or the menu is open. */}
        <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-success ring-2 ring-background-deep transition-colors group-hover:ring-row-hover group-data-[state=open]:ring-row-hover" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-sm font-semibold text-foreground">
          {session.display_name}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {session.role_label}
        </span>
      </div>
      <ChevronsUpDown
        size={14}
        strokeWidth={1.75}
        className="shrink-0 text-text-dim transition-transform group-hover:text-foreground group-data-[state=open]:rotate-180"
      />
    </>
  );

  const triggerClass = compact
    ? "relative flex h-10 w-10 items-center justify-center rounded-md transition-colors hover:bg-row-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-row-hover"
    : cn(
        // Flush row — the surrounding footer (with its top-border separator)
        // already frames the widget, so a second card with its own border +
        // surface fill was visually redundant. Sits as a hoverable row in the
        // section, lifts to `row-hover` on hover / open.
        "group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left",
        "transition-colors hover:bg-row-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "data-[state=open]:bg-row-hover",
      );

  const triggerButton = (
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={
          compact
            ? `Account menu — signed in as ${session.display_name}`
            : "Account menu"
        }
        className={triggerClass}
      >
        {triggerInner}
      </button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {compact ? (
        <Tooltip>
          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
          <TooltipContent side="right">
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold">
                {session.display_name}
              </span>
              <span className="text-xs text-muted-foreground">
                {session.role_label}
              </span>
            </div>
          </TooltipContent>
        </Tooltip>
      ) : (
        triggerButton
      )}

      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className={cn(
          "p-0 transition-[width] duration-150",
          // Prefs is a three-column grid (see `PreferencesView`); capped
          // to the viewport so it never overflows a phone.
          view === "prefs" ? "w-[min(820px,calc(100vw-2rem))]" : "w-[260px]",
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {view === "menu" && (
          <MenuView
            onPreferences={() => setView("prefs")}
            onSwitchTenant={() => setView("tenants")}
            onClose={() => setOpen(false)}
          />
        )}
        {view === "prefs" && <PreferencesView onBack={() => setView("menu")} />}
        {view === "tenants" && <TenantsView onBack={() => setView("menu")} />}
      </PopoverContent>
    </Popover>
  );
}

/** Top-level account menu — identity header, links, sign out. Rendered
 *  inside the shared popover surface; selecting Preferences swaps the
 *  surface to the rich settings view rather than opening a second one. */
function MenuView({
  onPreferences,
  onSwitchTenant,
  onClose,
}: {
  onPreferences: () => void;
  onSwitchTenant: () => void;
  onClose: () => void;
}) {
  const session = useAuthSession();
  const logout = useLogout();

  return (
    <div className="flex flex-col p-1">
      <div className="flex items-center gap-2.5 px-2 py-2">
        <InitialsAvatar
          name={session.display_name}
          initials={session.initials}
          size={32}
        />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-foreground">
            {session.display_name}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {session.role_label}
          </span>
        </div>
      </div>

      <Separator />

      <SectionLabel>Account</SectionLabel>
      <MenuRow icon={UserCircle} label="Profile" disabled />
      <MenuRow
        icon={Settings}
        label="Preferences"
        onSelect={onPreferences}
        showChevron
      />
      {session.is_super_admin && (
        <MenuRow
          icon={Building2}
          label="Switch tenant"
          onSelect={onSwitchTenant}
          showChevron
        />
      )}

      <Separator />

      <MenuRow
        icon={LogOut}
        label={logout.isPending ? "Signing out…" : "Sign out"}
        destructive
        disabled={logout.isPending}
        onSelect={() => {
          logout.mutate(undefined, {
            onSettled: () => onClose(),
          });
        }}
      />
    </div>
  );
}

/** Selectable row inside `MenuView` — replaces what `DropdownMenuItem`
 *  was doing before. Same hover treatment, same destructive variant,
 *  same disabled state. Optional chevron hints "this opens another
 *  view" (used by Preferences). */
function MenuRow({
  icon: Icon,
  label,
  onSelect,
  destructive,
  disabled,
  showChevron,
}: {
  icon: typeof UserCircle;
  label: string;
  onSelect?: () => void;
  destructive?: boolean;
  disabled?: boolean;
  showChevron?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect?.()}
      disabled={disabled}
      className={cn(
        "flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
        "focus-visible:bg-row-hover focus-visible:outline-none",
        destructive
          ? "text-destructive hover:bg-destructive-dim disabled:opacity-50"
          : "text-foreground hover:bg-row-hover disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      <Icon size={14} strokeWidth={1.75} />
      <span className="flex-1">{label}</span>
      {showChevron && (
        <ChevronsUpDown
          size={12}
          strokeWidth={1.75}
          className="rotate-90 text-text-dim"
        />
      )}
    </button>
  );
}

/** The tenants a super admin belongs to. Picking another one moves the
 *  session there and reloads the app in it; the current tenant is marked
 *  and not selectable. */
function TenantsView({ onBack }: { onBack: () => void }) {
  const tenants = useSessionTenants(true);
  const switchTenant = useSwitchTenant();

  return (
    <div className="flex flex-col p-1">
      <div className="flex items-center justify-between px-2 py-1.5">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={12} strokeWidth={2} />
          Back
        </button>
        <span className="text-sm font-semibold text-foreground">
          Switch tenant
        </span>
      </div>

      <Separator />

      {tenants.isLoading && (
        <div className="flex flex-col gap-1.5 px-2 py-1.5">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      )}
      {tenants.isError && (
        <p className="px-2 py-1.5 text-sm text-destructive">
          Could not load your tenants.
        </p>
      )}
      {(tenants.data ?? []).map((t) => (
        <button
          key={t.tenant_id}
          type="button"
          disabled={t.current || switchTenant.isPending}
          onClick={() => switchTenant.mutate(t.tenant_id)}
          className={cn(
            "flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
            "focus-visible:bg-row-hover focus-visible:outline-none",
            "text-foreground hover:bg-row-hover disabled:cursor-not-allowed",
          )}
        >
          <Building2 size={14} strokeWidth={1.75} />
          <span className="flex-1 truncate">{t.tenant_name}</span>
          {t.current && (
            <Check size={14} strokeWidth={2} className="text-primary" />
          )}
        </button>
      ))}
      {switchTenant.isError && (
        <p className="px-2 py-1.5 text-sm text-destructive">
          Could not switch to that tenant.
        </p>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 py-1.5 text-xs font-bold uppercase tracking-eyebrow text-text-dim">
      {children}
    </div>
  );
}

function Separator() {
  return <div className="-mx-1 my-1 h-px bg-border" />;
}

/** Rich preferences surface — palette swatch cards, mode toggle, guidance
 *  card. Lives inside the shared popover. The back button returns to
 *  `MenuView` without closing the popover. */
function PreferencesView({ onBack }: { onBack: () => void }) {
  const { mode, setMode, palette, setPalette } = useTheme();
  const [guidanceHidden, setGuidanceHidden] = useGuidanceVisibility();
  const { open: maxwellOpen, setOpen: setMaxwellOpen } = useMaxwellSurface();
  const maxwellOn = useMaxwellAccess();
  const session = useAuthSession();
  const [pricingUnit, setPricingUnit] = usePricingUnit(
    session.default_pricing_unit,
  );
  const [gasUnit, setGasUnit] = useGasUnit(session.user_id);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={12} strokeWidth={2} />
          Back
        </button>
        <div className="flex items-center gap-2">
          <Settings size={14} strokeWidth={1.75} className="text-text-dim" />
          <span className="text-sm font-semibold text-foreground">
            Preferences
          </span>
        </div>
      </div>

      {/* Three columns, each pairing two related settings, so the panel
          reads across rather than down: how it LOOKS, how numbers READ,
          and what the WORKSPACE shows. Stacked in one scrolling column on
          phones, where the popover is only as wide as the screen. */}
      <div className="grid max-h-[80vh] grid-cols-1 overflow-y-auto sm:grid-cols-3 sm:divide-x sm:divide-border">
        <PrefColumn title="Look">
          <PrefSection
            label="Appearance"
            description="Choose how the interface looks."
          >
            <ModeToggle mode={mode} onChange={setMode} />
          </PrefSection>

          <PrefSection
            label="Palette"
            description="Recolor the chrome — status colors and charts ride through."
          >
            <PaletteGrid palette={palette} onChange={setPalette} />
          </PrefSection>
        </PrefColumn>

        <PrefColumn title="Units">
          <PrefSection
            label="Electric"
            description="How electric rates are presented across the app."
          >
            <DisplayUnitCard unit={pricingUnit} onChange={setPricingUnit} />
          </PrefSection>

          <PrefSection
            label="Gas"
            description="How natural gas volumes and rates are presented across the app."
          >
            <GasUnitCard unit={gasUnit} onChange={setGasUnit} />
          </PrefSection>
        </PrefColumn>

        <PrefColumn title="Workspace">
          {maxwellOn ? (
            <PrefSection
              label="Maxwell"
              description="Show or hide the Maxwell assistant panel."
            >
              <MaxwellToggleCard
                hidden={!maxwellOpen}
                onChange={(next) => setMaxwellOpen(!next)}
              />
            </PrefSection>
          ) : null}

          <PrefSection
            label="Guidance"
            description="Setup checklists and inline hints across pages."
          >
            <GuidanceCard
              hidden={guidanceHidden}
              onChange={(next) => setGuidanceHidden(next)}
            />
          </PrefSection>
        </PrefColumn>
      </div>
    </div>
  );
}

/** One column of the preferences grid — a heading naming what its settings
 *  have in common, then the settings. A top border separates columns when
 *  they stack on phones; the grid's `divide-x` separates them side by side. */
function PrefColumn({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 border-t border-border p-4 first:border-t-0 sm:border-t-0">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      {children}
    </div>
  );
}

function PrefSection({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h3 className="text-xs font-bold uppercase tracking-eyebrow text-text-dim">
          {label}
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** Two-up segmented control for Dark / Light. Bigger hit target than a
 *  list row, gives a clear "current state" feel that suits a top-level
 *  setting. */
function ModeToggle({
  mode,
  onChange,
}: {
  mode: Mode;
  onChange: (next: Mode) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {MODE_VARIANTS.map((v) => {
        const Icon = v.icon;
        const active = mode === v.id;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onChange(v.id)}
            aria-pressed={active}
            className={cn(
              "flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-all",
              active
                ? "border-primary bg-primary-dim text-primary"
                : "border-border bg-surface-elevated text-muted-foreground hover:border-border-strong hover:text-foreground",
            )}
          >
            <Icon size={14} strokeWidth={1.75} />
            {v.label}
          </button>
        );
      })}
    </div>
  );
}

/** Grid of palette swatch cards. Each card shows a tiny preview of the
 *  three chrome colors (background, surface, primary) and the palette
 *  name. Active card carries a teal ring + check. Hover lifts the card.
 *  Same registry pattern as the old `ThemeMenu` — adding a palette to
 *  `PALETTE_VARIANTS` + the swatch lookup below picks it up automatically. */
function PaletteGrid({
  palette,
  onChange,
}: {
  palette: PaletteId;
  onChange: (next: PaletteId) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {PALETTE_VARIANTS.map((v) => {
        const swatch = PALETTE_SWATCHES[v.id];
        const active = palette === v.id;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onChange(v.id)}
            aria-pressed={active}
            className={cn(
              "group relative flex flex-col items-stretch gap-2 rounded-md border bg-card p-2 text-left transition-all",
              active
                ? "border-primary ring-2 ring-primary-dim"
                : "border-border hover:border-border-strong hover:shadow-card",
            )}
          >
            <div className="flex h-10 overflow-hidden rounded">
              <div
                className="flex-1"
                style={{ background: swatch.background }}
              />
              <div
                className="flex-1"
                style={{ background: swatch.surface }}
              />
              <div className="flex-1" style={{ background: swatch.primary }} />
            </div>
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "text-xs font-medium",
                  active ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {v.label}
              </span>
              {active && (
                <Check size={12} className="text-primary" strokeWidth={2.5} />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Toggle card for the Maxwell panel's visibility — a single full-width row
 *  with an on/off switch-style affordance. "Shown" vs "Hidden" is the
 *  user-facing flip; the underlying stored state is `hidden`, which we invert
 *  here for clarity. */
/** Unit picker + live preview for the user's electric rate display unit
 *  (AB#8329). Commits on select — no Save button, matching every other
 *  control in this panel. `SAMPLE_RATE` is a representative supply rate in
 *  the canonical `$/kWh`, so the preview is the real formatter output. */
function DisplayUnitCard({
  unit,
  onChange,
}: {
  unit: PricingUnit;
  onChange: (unit: PricingUnit) => void;
}) {
  const SAMPLE_RATE = 0.0739;
  return (
    <div className="flex items-end gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Label htmlFor="pref-pricing-unit" className="text-xs">
          Unit
        </Label>
        <Select
          value={unit}
          onValueChange={(v) => onChange(v as PricingUnit)}
        >
          <SelectTrigger id="pref-pricing-unit" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRICING_UNITS.map((u) => (
              <SelectItem key={u} value={u}>
                {pricingUnitLabel(u)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex shrink-0 flex-col gap-1.5">
        <span className="text-xs text-muted-foreground">Preview</span>
        <span className="flex h-9 items-center whitespace-nowrap text-sm font-medium text-foreground">
          {formatRateInUnit(SAMPLE_RATE, unit)}
        </span>
      </div>
    </div>
  );
}

/** Unit picker + live preview for the user's natural gas display unit
 *  (AB#8545). Commits on select, like the electric card above it. Until the
 *  user picks, every gas surface keeps its own unit and the picker reads
 *  "Default". `SAMPLE_RATE` is a representative gas supply rate in the
 *  canonical `$/therm`, so the preview is the real formatter output. */
function GasUnitCard({
  unit,
  onChange,
}: {
  unit: GasUnit | null;
  onChange: (unit: GasUnit) => void;
}) {
  const SAMPLE_RATE = 0.512;
  return (
    <div className="flex items-end gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Label htmlFor="pref-gas-unit" className="text-xs">
          Unit
        </Label>
        <Select value={unit ?? ""} onValueChange={(v) => onChange(v as GasUnit)}>
          <SelectTrigger id="pref-gas-unit" className="w-full">
            <SelectValue placeholder="Default" />
          </SelectTrigger>
          <SelectContent>
            {GAS_UNITS.map((u) => (
              <SelectItem key={u} value={u}>
                {GAS_UNIT_PICKER_LABELS[u]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex shrink-0 flex-col gap-1.5">
        <span className="text-xs text-muted-foreground">Preview</span>
        <span className="flex h-9 items-center whitespace-nowrap text-sm font-medium text-foreground">
          {formatGasRateIn(SAMPLE_RATE, unit)}
        </span>
      </div>
    </div>
  );
}

function MaxwellToggleCard({
  hidden,
  onChange,
}: {
  hidden: boolean;
  onChange: (hidden: boolean) => void;
}) {
  const shown = !hidden;
  return (
    <button
      type="button"
      onClick={() => onChange(!hidden)}
      aria-pressed={shown}
      className={cn(
        "flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2.5 text-left transition-all",
        shown
          ? "border-primary-mid bg-primary-dim"
          : "border-border hover:border-border-strong",
      )}
    >
      <div className="flex items-center gap-2.5">
        <Sparkles
          size={16}
          strokeWidth={1.75}
          className={shown ? "text-primary" : "text-text-dim"}
        />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-medium text-foreground">
            {shown ? "Shown" : "Hidden"}
          </span>
          <span className="text-xs text-muted-foreground">
            The Maxwell assistant panel
          </span>
        </div>
      </div>
      <span
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
          shown ? "bg-primary" : "bg-switch-track-off",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 rounded-full bg-card shadow-sm transition-transform",
            shown ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </span>
    </button>
  );
}

/** Palette swatch lookup — three representative colors per palette, used
 *  to render the visual preview on each palette card. Source-of-truth
 *  values mirror the palette blocks in `src/index.css`; if a palette's
 *  light/dark background diverges sharply, the light values are shown
 *  (the picker has higher legibility on the lighter swatch). */
const PALETTE_SWATCHES: Record<
  PaletteId,
  { background: string; surface: string; primary: string }
> = {
  default: {
    // The editorial Outlet look — parchment surfaces, deep teal primary.
    background: "#fafaf7",
    surface: "#f2f0eb",
    primary: "#006b5c",
  },
  classic: {
    background: "#ffffff",
    surface: "#f4f6f8",
    primary: "#02b795",
  },
  modern: {
    // Tailwind-ish dark-first ramp — the previous unclassed default.
    background: "#0b1715",
    surface: "#142a27",
    primary: "#2dd4bf",
  },
  mono: {
    background: "#ffffff",
    surface: "#f5f5f5",
    primary: "#0a0a0a",
  },
};

/** Toggle card for guidance (setup checklist) visibility — a single full-width
 *  row with an on/off switch-style affordance. "Shown" vs "Hidden" is the
 *  user-facing flip; the underlying stored state is `hidden`, which we invert
 *  here for clarity. Visually identical to `MaxwellToggleCard` (the two share
 *  the `guidanceHidden` preference) except for the icon and subtitle. */
function GuidanceCard({
  hidden,
  onChange,
}: {
  hidden: boolean;
  onChange: (hidden: boolean) => void;
}) {
  const shown = !hidden;
  return (
    <button
      type="button"
      onClick={() => onChange(!hidden)}
      aria-pressed={shown}
      className={cn(
        "flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2.5 text-left transition-all",
        shown
          ? "border-primary-mid bg-primary-dim"
          : "border-border hover:border-border-strong",
      )}
    >
      <div className="flex items-center gap-2.5">
        <Compass
          size={16}
          strokeWidth={1.75}
          className={shown ? "text-primary" : "text-text-dim"}
        />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-medium text-foreground">
            {shown ? "Shown" : "Hidden"}
          </span>
          <span className="text-xs text-muted-foreground">
            Setup checklists across the app
          </span>
        </div>
      </div>
      <span
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
          shown ? "bg-primary" : "bg-switch-track-off",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 rounded-full bg-card shadow-sm transition-transform",
            shown ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </span>
    </button>
  );
}
